// HELPEE RELATION OVERVIEW
<script setup lang="ts">
// NPM
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isAxiosError } from 'axios';

// Components
import { BaseAvatar, BaseBadge, BaseIcon, getDisplayName, getInitials } from '@magma-app/magma-lapilli';
import BaseRelationMoment from '@/components/bases/BaseRelationMoment.vue';

// Types
import type { ITouchPointNoId } from '@/types/ITouchPoint';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Composables & Stores
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import {
  copyOrOpenDiscordTouchpoint,
  isDiscordTouchpoint,
} from '@/utils/discord';
import type { ProspectDashboardGetRelationResponseType } from '@magma-app/magma-types/all';

const commonStore = useCommonStore();
const relationsStore = useRelationsStore();
const userStore = useUserStore();

// Plugins
const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n({ useScope: 'global' });

// Reactive variables
const initialized = ref(false);
const relation = ref<ProspectDashboardGetRelationResponseType | null>(null);
const isHandlingOngoingQuery = ref(false);
let unavailableMomentHandled = false;

// Computed properties
type RelationMomentStatus = 'ongoing' | 'waitingAdminValidation' | 'validated';

const relationId = computed(() =>
  route?.params.relationId
    ? Number(route?.params.relationId as string)
    : undefined
);

const relationMomentStatus = computed<RelationMomentStatus>(() => {
  const status = relation.value?.relation?.relationMoment.status as
    | string
    | undefined;
  if (
    status &&
    ['ongoing', 'waitingAdminValidation', 'validated'].includes(status)
  ) {
    return status as RelationMomentStatus;
  }

  // Keep a safe default for invalid/empty statuses.
  return 'ongoing';
});

const shouldRedirectToReport = computed(
  () => false
);

const isArchivedNotReported = computed(
  () => relation.value?.isActive === false && !relation.value?.isReported
);

const isRelationClosed = computed(
  () => !!relation.value?.relation?.metaStatus
);

const canReportRelation = computed(() => {
  return (
    !relation.value?.isReported
    && relation.value?.isActive !== false
    && !isRelationClosed.value
  );
});

const helperFullName = computed(() => {
  const helper = relation.value?.relation?.helper;
  return helper ? getDisplayName(helper.firstname, helper.lastname, helper.email) : '';
});

const helperAvatarFallback = computed(() => {
  const helper = relation.value?.relation?.helper;
  return helper
    ? getInitials(helper.firstname, helper.lastname, helper.email)
    : '';
});

const showUnavailableRelation = () => {
  commonStore.setAlert({
    open: true,
    type: 'error',
    title: t('actionDeepLink.notFound.title'),
    description: t('actionDeepLink.notFound.relation'),
    duration: 5000,
  });
};

const rejectUnavailableMoment = async () => {
  if (!('moment' in route.query) || unavailableMomentHandled) return false;

  const requestedMomentId = Number(
    Array.isArray(route.query.moment) ? route.query.moment[0] : route.query.moment
  );
  const currentMomentId =
    relation.value?.relation?.relationMoment?.relationMomentId;
  if (
    Number.isInteger(requestedMomentId) &&
    requestedMomentId > 0 &&
    requestedMomentId === currentMomentId
  ) {
    return false;
  }

  unavailableMomentHandled = true;
  showUnavailableRelation();
  const query = { ...route.query };
  delete query.moment;
  delete query.helpee;
  await router.replace({ path: route.path, query });
  return true;
};

// Helper functions
const createFeedbackModalConfig = (
  relationMoment: NonNullable<
    ProspectDashboardGetRelationResponseType['relation']
  >['relationMoment'],
  helperFirstname: string,
  relationMomentId?: number
) => {
  const needsFeedback =
    relationMoment.prospect?.needFeedbackRating ||
    relationMoment.prospect?.needFeedbackComment ||
    relationMoment.prospect?.needFeedbackFile;

  const descriptionKey = needsFeedback
    ? 'generic.feedbackModalDescription'
    : 'helpeeRelationOverview.noFeedbackModalDescription';

  return {
    description: `${t(descriptionKey)}${helperFirstname}.`,
    needFeedbackRating: !!relationMoment.prospect?.needFeedbackRating,
    needFeedbackComment: !!relationMoment.prospect?.needFeedbackComment,
    needFeedbackFile: !!relationMoment.prospect?.needFeedbackFile,
    open: true,
    title: t('generic.doneModalTitle'),
    relationMomentId: relationMomentId || relationMoment.relationMomentId,
    helpeeName: userStore.user?.firstname || '',
    role: needsFeedback ? 'done-with-feedback' : 'done-without-feedback',
    type: 'prospect' as const,
    persona: 'prospect' as const,
  };
};

const handleContactQuery = () => {
  if (!relation.value) return;
  if (relation.value.isReported || relation.value.isActive === false || isRelationClosed.value) return;

  if (!relationsStore.relationHelpee?.hasBeenInContact) {
    router.push({
      name: 'start-relation-helpee',
      params: { relationId: route.params.relationId },
    });
  }
};

const handleOngoingQuery = async () => {
  if (!relation.value || isHandlingOngoingQuery.value) return;
  if (relation.value.isReported || relation.value.isActive === false || isRelationClosed.value) return;

  const queryMomentId = Number(route?.query.moment);
  const relationMoment = relation.value.relation?.relationMoment;

  if (!relationMoment || Number.isNaN(queryMomentId)) return;
  if (relationMoment.relationMomentId !== queryMomentId) return;

  if (
    !relationMoment.prospect?.isOngoing &&
    !relationMoment.helper?.isValidatedByHelper
  ) {
    isHandlingOngoingQuery.value = true;

    try {
      await relationsStore.markRelationMomentAs({
        relationMomentId: relationMoment.relationMomentId,
        role: 'ongoing',
        type: 'helpee',
      });

      const refreshedRelation = (await relationsStore.getRelation({
        relationId: relationId.value!,
        type: 'helpee',
      })) as ProspectDashboardGetRelationResponseType;

      relation.value = refreshedRelation;
    } finally {
      isHandlingOngoingQuery.value = false;
    }
  }
};

const handleDoneQuery = () => {
  if (!relation.value) return;
  if (relation.value.isReported || relation.value.isActive === false || isRelationClosed.value) return;

  const queryMomentId = Number(route?.query.moment);
  const relationMoment = relation.value.relation?.relationMoment;

  if (!relationMoment || Number.isNaN(queryMomentId)) return;
  if (relationMoment.relationMomentId !== queryMomentId) return;
  if (relationMoment.prospect?.isValidated || relationMoment.status !== 'ongoing') return;

  const modalConfig = createFeedbackModalConfig(
    relationMoment,
    relation.value.relation?.helper?.firstname || ''
  );

  commonStore.setFeedbackRelationModal(modalConfig);
};

// Helper function to load relation data
const loadRelationData = async () => {
  initialized.value = false;

  if (!relationId.value || !Number.isInteger(relationId.value)) {
    showUnavailableRelation();
    await router.replace({ name: 'relations-empty-state' });
    initialized.value = true;
    return;
  }

  try {
    const r = (await relationsStore.getRelation({
      relationId: relationId.value,
      type: 'helpee',
    })) as ProspectDashboardGetRelationResponseType;

    if (!r) return;
    relation.value = r;
  } catch (error) {
    console.error('Failed to fetch relation:', error);
    if (isAxiosError(error) && [403, 404].includes(error.response?.status || 0)) {
      showUnavailableRelation();
      await router.replace({ name: 'relations-empty-state' });
    }
  } finally {
    initialized.value = true;
  }
};

// Lifecycle and watchers
onMounted(async () => {
  await loadRelationData();
});

// Watch for route changes
watch(
  () => route.params.relationId,
  async (newRelationId) => {
    if (newRelationId) {
      await loadRelationData();
    }
  }
);

// Watch for report redirects
watch(shouldRedirectToReport, (shouldRedirect) => {
  if (shouldRedirect && relation.value) {
    router.push({
      name: 'report-relation-view',
      params: {
        relationId: relation.value.relationId,
        userType: 'helpee',
        reason: relation.value.report!.reason,
      },
    });
  }
});

// Watch for language and campaign setup
watch(
  relation,
  async (newRelation) => {
    if (!newRelation || newRelation.report) return;

    // Set language
    if (newRelation.language) {
      locale.value = newRelation.language;
    } else if (userStore.user?.language) {
      locale.value = userStore.user.language;
    }

    // Get campaign info
    try {
      await userStore.getCampaignInfoProspect(newRelation.relationId);
    } catch (error) {
      console.error('Failed to get campaign info:', error);
    }
  },
  { immediate: true }
);

// Watch for query-based actions
watch(
  [() => relation.value, () => route.query.helpee],
  async ([currentRelation, queryHelpee]) => {
    if (!currentRelation || !queryHelpee) return;
    if (await rejectUnavailableMoment()) return;

    switch (queryHelpee) {
      case 'contact':
        handleContactQuery();
        break;
      case 'ongoing':
        handleOngoingQuery();
        break;
      case 'done':
        handleDoneQuery();
        break;
    }
  },
  { immediate: true }
);

// Event listeners
const onHelpeeValidation = (
  relationOnValidation: ProspectDashboardGetRelationResponseType | null
) => {
  if (!relationOnValidation?.relation) return;

  const { relationMoment } = relationOnValidation.relation;

  const modalConfig = createFeedbackModalConfig(
    relationMoment,
    relationOnValidation.relation.helper?.firstname || ''
  );

  commonStore.setFeedbackRelationModal(modalConfig);

  AnalyticsHelper.trackEvent('validate_moment', {
    event_properties: 'timeline',
    user_properties: 'helpee',
  });
};

const onReport = () => {
  if (!canReportRelation.value) return;

  commonStore.setReportModal({
    open: true,
    role: 'report',
    type: 'helpee',
    title: t('generic.reportTitle'),
    description: t('generic.reportDescription'),
    helperName: relation.value?.relation?.helper?.firstname,
  });
};

const openMailProvider = (email: string | null) => {
  const sanitizedEmail = email?.trim();
  if (!sanitizedEmail) return;

  const mailtoUrl = sanitizedEmail.startsWith('mailto:')
    ? sanitizedEmail
    : `mailto:${sanitizedEmail}`;

  // Use _self to avoid creating a blank tab when no default mail provider is configured.
  window.open(mailtoUrl, '_self');
};

const onTouchPointClick = async (touchPoint: ITouchPointNoId) => {
  if (!relationsStore.relationHelpee?.isReported) {
    if (['line', 'phone'].includes(touchPoint.name)) {
      commonStore.setCallTextModal({
        helpeeName: userStore.user?.firstname || '',
        helperName: relation.value?.relation?.helper?.firstname || '',
        open: true,
        touchpoint: touchPoint.value,
      });
    }

    if (touchPoint.name === 'whatsapp') {
      const phoneNumber = touchPoint.value?.replace(/\+/g, '');
      window.open(
        `https://api.whatsapp.com/send?phone=${phoneNumber}`,
        '_blank'
      );
    }

    if (touchPoint.name === 'mail') {
      openMailProvider(touchPoint.value);
    }

    if (touchPoint.type === 'pseudo' && touchPoint.value) {
      if (isDiscordTouchpoint(touchPoint.name)) {
        const copied = await copyOrOpenDiscordTouchpoint(touchPoint.value);

        if (copied) {
          commonStore.setAlert({
            open: true,
            type: 'success',
            title: t('generic.copied'),
          });
        }
      } else {
        window.open(touchPoint.value, '_blank');
      }
    }

    AnalyticsHelper.trackEvent('send_message', {
      event_properties: 'touchpoints',
      user_properties: 'helpee',
    });
  }
};
</script>

<template>
  <div
    :class="[
      'flex h-full w-full flex-1 flex-col',
      {
        'bg-background-subtle lg:pl-3 lg:pt-3':
          !relationsStore.relationHelpee?.isReported && !isArchivedNotReported
      }
    ]"
    data-cy="helpee-relation"
  >
    <header
      v-if="relationsStore.relationHelpee?.isReported || isArchivedNotReported"
      class="hidden sm:flex sm:items-center"
    >
      <div class="flex items-center px-20 py-5">
        <BaseAvatar
          :src="userStore.campaignInfo?.logoUrl || ''"
          :alt="'logo ' + userStore.campaignInfo?.organizationName"
          :fallback="userStore.campaignInfo?.organizationName?.charAt(0)"
        />
        <h1
          class="max-w-[300px truncate px-2 text-mgm-txt-sm font-medium text-foreground-emphasis lg:max-w-[300px]"
        >
          {{ userStore.campaignInfo?.organizationName }}
        </h1>
      </div>
    </header>

    <div
      :class="[
        relationsStore.relationHelpee?.isReported || isArchivedNotReported
          ? 'min-h-screen bg-background-subtle-hover sm:pt-10'
          : 'flex flex-1 flex-col bg-background-default ' +
            'lg:rounded-tl-[32px] lg:border lg:border-border-subtle ' +
            'lg:border-b-transparent lg:border-r-transparent lg:pt-8'
      ]"
    >
      <div
        v-if="initialized"
        :class="[
          'min-h-[95vh] w-full bg-background-default sm:rounded-2xl ' +
            'sm:shadow-[0px_4px_6px_-2px_rgba(28,31,39,0.03),0px_12px_16px_-4px_rgba(28,31,39,0.08)]',
          {
            'mx-auto max-w-[560px] sm:h-fit sm:min-h-fit':
              relationsStore.relationHelpee?.isReported || isArchivedNotReported
          }
        ]"
      >
        <template v-if="isArchivedNotReported">
          <header class="flex items-center p-4 sm:hidden">
            <div class="flex items-center">
              <BaseAvatar
                :src="relation?.organization?.logoUrl || userStore.campaignInfo?.logoUrl || ''"
                :alt="'logo ' + (relation?.organization?.name || userStore.campaignInfo?.organizationName)"
                :fallback="(relation?.organization?.name || userStore.campaignInfo?.organizationName)?.charAt(0)"
              />

              <h1
                class="max-w-[300px] truncate px-2 text-mgm-txt-sm font-medium text-foreground-emphasis lg:max-w-[300px]"
              >
                {{ relation?.organization?.name || userStore.campaignInfo?.organizationName }}
              </h1>
            </div>
          </header>

          <main class="px-4 py-6">
            <BaseIcon
              class="block max-w-fit border-8 border-background-subtle"
              :size="24"
              bg-color="#F1F5F9"
              color="#5F6C85"
              icon="info"
            />

            <h2
              class="mt-4 text-mgm-txt-lg font-semibold text-foreground-emphasis"
            >
              {{ $t('helpeeRelationOverview.archived.title') }}
            </h2>

            <p class="mt-2 text-mgm-txt-sm font-normal text-foreground-default">
              {{ $t('helpeeRelationOverview.archived.description') }}
            </p>
          </main>
        </template>

        <template v-else-if="!relationsStore.relationHelpee?.isReported">
          <div class="mb-2 border-b border-border-subtle p-4 md:px-16">
            <div class="flex items-center justify-between">
              <span
                class="text-mgm-txt-lg font-medium text-foreground-emphasis"
              >
                {{ helperFullName }}
              </span>

              <BaseIcon
                v-if="canReportRelation"
                :size="24"
                color="#5F6C85"
                icon="flag"
                hover-state
                @click="onReport"
              />
            </div>

            <div class="mb-2 mt-3 flex justify-between">
              <BaseAvatar
                :src="relation?.relation?.helper?.profilePictureUrl"
                :fallback="helperAvatarFallback"
                size="xl"
              />

              <div
                v-if="relation?.relation?.helper?.criteria?.length"
                class="ml-4 flex-1 text-mgm-txt-sm font-normal text-foreground-default"
              >
                <p
                  v-for="criterium of relation?.relation?.helper.criteria"
                  :key="criterium.value"
                  class="mb-1 last:mb-0"
                >
                  <!-- 05/01/2026: we decided to stop displaying the question on the helpee side -->
                  <!-- {{
                    `${determinePunctuation(criterium.labelHelper || "", ":", ["?"])}`
                  }} -->
                  <span class="font-medium">{{ criterium.value }}</span>
                </p>
              </div>
            </div>

            <div
              v-if="relation?.relation?.helper?.touchPoints?.length"
              class="flex flex-wrap"
            >
              <BaseBadge
                v-for="touchPoint of relation?.relation?.helper?.touchPoints"
                :key="touchPoint.name"
                :text="touchPoint.name"
                :class="[
                  'mr-2 mt-2 last:mr-0',
                  relation?.isReported ? 'cursor-default' : 'cursor-pointer'
                ]"
                size="lg"
                color="grey"
                icon-type="custom"
                @click="onTouchPointClick(touchPoint)"
              >
                <template #icon>
                  <BaseAvatar
                    :src="touchPoint.icon"
                    size="xs"
                    shape="squared"
                  />
                </template>
              </BaseBadge>
            </div>
          </div>

          <div class="px-4 pb-6 pt-4 md:mb-8 md:mt-6 md:px-16">
            <BaseRelationMoment
              :relation-moment-id="
                relation?.relation?.relationMoment.relationMomentId as number
              "
              :status-relation="null"
              :status-relation-moment="relationMomentStatus"
              :instruction-feedback="
                relation?.relation?.relationMoment.description || ''
              "
              :description-campaign="relation?.description || ''"
              :need-feedback-rating="
                relation?.relation?.relationMoment.prospect?.needFeedbackRating
              "
              :need-feedback-comment="
                relation?.relation?.relationMoment.prospect?.needFeedbackComment
              "
              :need-feedback-file="
                relation?.relation?.relationMoment.prospect?.needFeedbackFile
              "
              :disable-validation="isRelationClosed"
              :persona="'prospect'"
              @helpee-validation="onHelpeeValidation"
            />
          </div>
        </template>

        <template v-else-if="relationsStore.relationHelpee?.isReported">
          <header class="flex items-center p-4 sm:hidden">
            <div class="flex items-center">
              <BaseAvatar
                :src="userStore.campaignInfo?.logoUrl || ''"
                :alt="'logo ' + userStore.campaignInfo?.organizationName"
                :fallback="userStore.campaignInfo?.organizationName?.charAt(0)"
              />

              <h1
                class="max-w-[300px truncate px-2 text-mgm-txt-sm font-medium text-foreground-emphasis lg:max-w-[300px]"
              >
                {{ userStore.campaignInfo?.organizationName }}
              </h1>
            </div>
          </header>

          <main class="px-4 py-6">
            <BaseIcon
              class="block max-w-fit border-8 border-background-success-default"
              :size="24"
              bg-color="#D1FADF"
              color="#03874C"
              icon="check"
            />

            <h2
              class="tetx-foreground-emphasis mt-4 text-mgm-txt-lg font-semibold"
            >
              {{
                `${$t('helpeeRelationOverview.helpee.title.partOne')} ${
                  relation?.report?.firstname
                } ${$t('helpeeRelationOverview.helpee.title.partTwo')}`
              }}
            </h2>

            <p class="mt-2 text-mgm-txt-sm font-normal text-foreground-default">
              {{
                `${
                  relation?.report?.reason === 'ghosting'
                    ? `${$t(
                        'helpeeRelationOverview.helpee.description.partOne'
                      )} ${relation?.report?.firstname}.`
                    : relation?.report?.reason === 'no-interest'
                      ? $t(
                          'helpeeRelationOverview.helpee.description.notInterested'
                        )
                      : `${$t(
                          'helpeeRelationOverview.reportRedirection.title'
                        )}.`
                } ${$t('helpeeRelationOverview.helpee.description.partTwo')}`
              }}
            </p>

            <p
              v-if="relation?.report?.askRematch"
              class="mt-4 text-mgm-txt-sm font-normal text-foreground-default"
            >
              {{ $t('helpeeRelationOverview.helpee.description.partThree') }}
            </p>

            <p class="mt-4 text-mgm-txt-sm font-normal text-foreground-default">
              {{ $t('helpeeRelationOverview.helpee.description.partFour') }}
            </p>

            <ul
              class="mt-4 list-none rounded-[5px] bg-background-subtle p-4 text-mgm-txt-sm font-normal text-foreground-emphasis"
            >
              <li>
                <h3 class="text-foreground-default">
                  {{ $t('helpeeRelationOverview.report.helpee.reason.label') }}
                </h3>
                <p>
                  {{
                    relation?.report?.reason === 'ghosting'
                      ? `${relation?.report?.firstname} ${$t(
                          'helpeeRelationOverview.reportReasons.ghosting.title'
                        )}`
                      : relation?.report?.reason === 'no-interest'
                        ? $t('generic.notInterested')
                        : $t('generic.other')
                  }}
                </p>
              </li>

              <li v-if="relation?.report?.contactedAt" class="mt-4">
                <h3 class="text-foreground-default">
                  {{
                    `${$t(
                      'helpeeRelationOverview.reportForm.ghosting.when.label'
                    )} ${relation?.report?.firstname}${
                      $i18n.locale === 'fr' ? ' ' : ''
                    }?`
                  }}
                </h3>
                <p>
                  {{
                    new Date(relation?.report?.contactedAt)?.toLocaleDateString(
                      $i18n.locale,
                      {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }
                    )
                  }}
                </p>
              </li>

              <li class="mt-4">
                <h3 class="text-foreground-default">
                  {{
                    $t(
                      'helpeeRelationOverview.reportForm.ghosting.message.label'
                    )
                  }}
                </h3>
                <p>{{ relation?.report?.comment }}</p>
              </li>

              <li v-if="relation?.report?.askRematch" class="mt-4">
                <h3 class="text-foreground-default">
                  {{ $t('generic.rematch.label') }}
                </h3>
                <p>
                  {{
                    $t(
                      `generic.rematch.${
                        relation?.report?.askRematch ? 'yes' : 'no'
                      }`
                    )
                  }}
                </p>
              </li>
            </ul>
          </main>
        </template>
      </div>
    </div>
  </div>
</template>
